import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, 
  Shield, 
  UserX,
  UserCheck,
  AlertTriangle,
  RefreshCw,
  Search,
  Eye,
  X,
  Clock,
  FileText,
  UserPlus,
  Pencil,
  Send,
  Copy
} from 'lucide-react';

const ASSIGNABLE_ROLES = ['staff_viewer', 'dispatcher', 'safety_compliance_admin', 'payroll_admin', 'fleet_manager', 'mechanic', 'accounting', 'operations_admin', 'maintenance_manager', 'accountant', 'admin', 'ops_admin'];
const CLASSIFICATIONS = ['', 'W2 Employee', 'Independent Contractor', 'Company Driver', 'Lease-On', 'Dispatch-Only', 'Contractor/Vendor'];

const SUSPENSION_CATEGORIES = [
  "Policy Violation",
  "Compliance Issue",
  "Performance Review",
  "Security Concern",
  "Temporary Leave",
  "Administrative Hold",
  "Other"
];

// Staff roles that should appear in Staff Directory
const STAFF_ROLES = ['super_admin', 'ops_admin', 'admin', 'dispatcher', 'safety_compliance_admin', 'payroll_admin', 'fleet_manager', 'mechanic', 'accounting', 'operations_admin', 'staff_viewer', 'maintenance_manager', 'accountant'];

const StaffDirectoryPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [suspensionDetails, setSuspensionDetails] = useState(null);
  
  // Suspension form
  const [suspendCategory, setSuspendCategory] = useState('');
  const [suspendNotes, setSuspendNotes] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Check if current user can suspend/reactivate
  const canManageSuspension = ['super_admin', 'ops_admin'].includes(user?.role);
  const canManageStaff = ['super_admin', 'ops_admin'].includes(user?.role);
  const isSuperAdminUser = user?.role === 'super_admin';

  // Add / Edit staff modal
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffMode, setStaffMode] = useState('add');
  const [staffForm, setStaffForm] = useState({});
  const [staffSaving, setStaffSaving] = useState(false);
  const [staffError, setStaffError] = useState(null);

  const openAddStaff = () => {
    setStaffMode('add'); setStaffError(null);
    setStaffForm({ first_name: '', last_name: '', email: '', phone: '', title: '', department: '', manager: '', workforce_classification: '', role: 'staff_viewer', start_date: '', internal_notes: '' });
    setShowStaffModal(true);
  };
  const openEditStaff = (u) => {
    setStaffMode('edit'); setStaffError(null);
    setStaffForm({ id: u.id, first_name: u.first_name || '', last_name: u.last_name || '', email: u.email || '',
      phone: u.phone || '', title: u.title || '', department: u.department || '', manager: u.manager || '',
      workforce_classification: u.workforce_classification || '', start_date: u.start_date || '',
      internal_notes: u.internal_notes || '', is_active: u.is_active !== false });
    setShowStaffModal(true);
  };
  const sf = (k) => (e) => setStaffForm((f) => ({ ...f, [k]: e.target.value }));
  const saveStaff = async () => {
    setStaffError(null); setStaffSaving(true);
    try {
      if (staffMode === 'add') {
        await adminService.createStaff(staffForm);
      } else {
        const { id, email, ...editable } = staffForm;
        await adminService.updateStaff(id, editable);
      }
      setShowStaffModal(false);
      await loadUsers();
    } catch (err) {
      const d = err.response?.data?.detail;
      setStaffError(typeof d === 'string' ? d : (d?.message || 'Failed to save staff member'));
    } finally { setStaffSaving(false); }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    // Clear any stale role cache - re-read token fresh
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setError('Authentication required. Please log in again.');
      setLoading(false);
      return;
    }

    setError(null);
    try {
      setLoading(true);
      
      // Use the unified staff directory endpoint (SINGLE SOURCE OF TRUTH)
      // Always include inactive so we can show them with the Archived filter
      const data = await adminService.getStaffDirectory(true);
      
      // API returns { staff: [...], total: N, roles_included: [...] }
      const staffUsers = data.staff || [];
      
      // Store ALL staff users - filtering happens in filteredUsers based on filterStatus
      setUsers(staffUsers);
      console.log(`Staff directory loaded: ${staffUsers.length} users`);
    } catch (err) {
      console.error('Failed to load staff directory:', err);
      // Fallback to getAllUsers if new endpoint fails
      try {
        console.log('Falling back to getAllUsers endpoint...');
        const fallbackData = await adminService.getAllUsers();
        const userList = Array.isArray(fallbackData) ? fallbackData : (fallbackData.users || []);
        const staffUsers = userList.filter(u => STAFF_ROLES.includes(u.role));
        setUsers(staffUsers);
      } catch (fallbackErr) {
        setError(`Failed to load staff directory: ${err.message || 'Unknown error'}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get canonical display name for a user
   * Priority: display_name > first_name + last_name > email prefix
   * NEVER fall back to role name or generic label
   */
  const getUserDisplayName = (u) => {
    // First priority: explicit display_name
    if (u.display_name && u.display_name.trim()) {
      return u.display_name.trim();
    }
    // Second priority: first_name + last_name combination
    const firstName = (u.first_name || '').trim();
    const lastName = (u.last_name || '').trim();
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    // Last resort: email prefix (never role name)
    return u.email?.split('@')[0] || 'Unknown User';
  };

  const getUserStatus = (u) => {
    if (u.status === 'suspended' || !u.is_active) return 'suspended';
    return 'active';
  };

  const getStatusBadge = (u) => {
    const status = getUserStatus(u);
    if (status === 'suspended') {
      return <Badge className="bg-red-100 text-red-800">Suspended</Badge>;
    }
    return <Badge className="bg-green-100 text-green-800">Active</Badge>;
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-800',
      ops_admin: 'bg-blue-100 text-blue-800',
      admin: 'bg-indigo-100 text-indigo-800',
      dispatcher: 'bg-cyan-100 text-cyan-800',
      driver: 'bg-gray-100 text-gray-800',
      fleet_manager: 'bg-orange-100 text-orange-800',
      accounting: 'bg-green-100 text-green-800'
    };
    const labels = {
      super_admin: 'Super Admin',
      ops_admin: 'Ops Admin',
      admin: 'Admin',
      dispatcher: 'Dispatcher',
      driver: 'Driver',
      fleet_manager: 'Fleet Manager',
      accounting: 'Accounting'
    };
    return (
      <Badge className={colors[role] || 'bg-gray-100 text-gray-800'}>
        {labels[role] || role}
      </Badge>
    );
  };

  const handleSuspendClick = (u) => {
    setSelectedUser(u);
    setSuspendCategory('');
    setSuspendNotes('');
    setShowSuspendModal(true);
  };

  const handleViewDetails = async (u) => {
    setSelectedUser(u);
    try {
      const details = await adminService.getSuspensionDetails(u.id);
      setSuspensionDetails(details);
      setShowDetailsModal(true);
    } catch (err) {
      console.error('Failed to get suspension details:', err);
      alert('Failed to load suspension details');
    }
  };

  const handleSuspend = async () => {
    if (!suspendCategory) {
      alert('Please select a suspension category');
      return;
    }
    if (suspendCategory === 'Other' && !suspendNotes) {
      alert('Notes are required when category is "Other"');
      return;
    }

    try {
      setActionLoading(true);
      await adminService.suspendUser(selectedUser.id, suspendCategory, suspendNotes || null);
      alert('User suspended successfully');
      setShowSuspendModal(false);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (u) => {
    if (!confirm(`Reactivate ${getUserDisplayName(u)}? Their original role and permissions will be restored.`)) {
      return;
    }

    try {
      setActionLoading(true);
      const result = await adminService.reactivateUser(u.id);
      alert(result.message || 'User reactivated successfully');
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reactivate user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleInvitePortal = async (u) => {
    if (!confirm(`Invite ${getUserDisplayName(u)} to portal access?\n\nThis grants LOGIN only. It does NOT change their role, workforce classification, readiness, or operational authorization. They set their own password via a secure link.`)) {
      return;
    }
    try {
      setActionLoading(true);
      const result = await adminService.inviteStaffPortal(u.id);
      setInviteResult({ name: getUserDisplayName(u), ...result });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to invite staff to portal');
    } finally {
      setActionLoading(false);
    }
  };

  // Helper to check if user is archived/inactive
  const isArchivedOrInactive = (u) => {
    return u.status === 'removed' || u.status === 'archived' || 
           u.status === 'inactive' || u.status === 'deleted' ||
           (u.is_active === false && u.status !== 'suspended');
  };

  // Filter users - by default exclude archived/inactive unless specifically requested
  const filteredUsers = users.filter(u => {
    const name = getUserDisplayName(u).toLowerCase();
    const email = (u.email || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    const matchesSearch = name.includes(search) || email.includes(search);
    
    // "all" shows active + suspended only (not archived)
    if (filterStatus === 'all') {
      return matchesSearch && !isArchivedOrInactive(u);
    }
    if (filterStatus === 'active') {
      return matchesSearch && getUserStatus(u) === 'active' && !isArchivedOrInactive(u);
    }
    if (filterStatus === 'suspended') {
      return matchesSearch && getUserStatus(u) === 'suspended';
    }
    if (filterStatus === 'archived') {
      return matchesSearch && isArchivedOrInactive(u);
    }
    return matchesSearch && !isArchivedOrInactive(u);
  });

  // Stats - only count active staff (exclude archived)
  const activeStaff = users.filter(u => !isArchivedOrInactive(u));
  const activeCount = activeStaff.filter(u => getUserStatus(u) === 'active').length;
  const suspendedCount = activeStaff.filter(u => getUserStatus(u) === 'suspended').length;
  const archivedCount = users.filter(u => isArchivedOrInactive(u)).length;
  const superAdminCount = activeStaff.filter(u => u.role === 'super_admin' && getUserStatus(u) === 'active').length;
  const opsAdminCount = activeStaff.filter(u => u.role === 'ops_admin' && getUserStatus(u) === 'active').length;
  const adminCount = activeStaff.filter(u => ['admin', 'super_admin', 'ops_admin'].includes(u.role) && getUserStatus(u) === 'active').length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Error state with retry option
  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="flex flex-col items-center gap-4 max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-red-600 font-medium">Staff Directory Load Error</p>
            <p className="text-gray-500 text-sm">{error}</p>
            <p className="text-gray-400 text-xs">Role: {user?.role || 'Unknown'}</p>
            <Button
              onClick={() => { setError(null); loadUsers(); }}
              className="flex items-center gap-2"
              data-testid="retry-btn"
            >
              <RefreshCw className="w-4 h-4" /> Retry
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="staff-directory-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-blue-600" />
              Staff Directory
            </h1>
            <p className="text-gray-600 mt-1">Manage staff accounts and suspension status</p>
          </div>
          <div className="flex items-center gap-2">
            {canManageStaff && (
              <Button onClick={openAddStaff} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-staff-btn">
                <UserPlus className="w-4 h-4" /> Add Staff
              </Button>
            )}
            <Button onClick={loadUsers} variant="outline" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{activeStaff.length}</div>
                  <div className="text-sm text-gray-500">Total Staff</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <UserCheck className="w-8 h-8 text-green-600" />
                <div>
                  <div className="text-2xl font-bold">{activeCount}</div>
                  <div className="text-sm text-gray-500">Active</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <UserX className="w-8 h-8 text-red-600" />
                <div>
                  <div className="text-2xl font-bold">{suspendedCount}</div>
                  <div className="text-sm text-gray-500">Suspended</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold">{superAdminCount}</div>
                  <div className="text-sm text-gray-500">Super Admins</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{opsAdminCount}</div>
                  <div className="text-sm text-gray-500">Ops Admins</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-gray-500" />
                <div>
                  <div className="text-2xl font-bold text-gray-600">{archivedCount}</div>
                  <div className="text-sm text-gray-500">Archived</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="staff-search-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                {['all', 'active', 'suspended', 'archived'].map(status => (
                  <Button
                    key={status}
                    variant={filterStatus === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus(status)}
                    className="capitalize"
                  >
                    {status === 'archived' ? 'Archived/Inactive' : status}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Staff List */}
        <Card>
          <CardHeader>
            <CardTitle>Staff Members ({filteredUsers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No staff members found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-3 font-medium">Name</th>
                      <th className="text-left py-3 px-3 font-medium">Email</th>
                      <th className="text-left py-3 px-3 font-medium">Role</th>
                      <th className="text-left py-3 px-3 font-medium">Status</th>
                      <th className="text-left py-3 px-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => {
                      const isSuspended = getUserStatus(u) === 'suspended';
                      const isSelf = u.id === user?.sub;
                      const isSuperAdmin = u.role === 'super_admin';
                      const isOpsAdmin = u.role === 'ops_admin';
                      const isAdmin = ['super_admin', 'ops_admin', 'admin'].includes(u.role);
                      
                      return (
                        <tr 
                          key={u.id} 
                          className={`border-b hover:bg-gray-50 ${isSuspended ? 'bg-red-50' : ''} ${isAdmin && !isSuspended ? 'bg-blue-50/30' : ''}`}
                          data-testid={`staff-row-${u.id}`}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <div>
                                <div className="font-medium">{getUserDisplayName(u)}</div>
                                {u.display_role_label && (
                                  <div className={`text-xs ${isAdmin ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                                    {u.display_role_label}
                                    {u.display_subtitle && ` • ${u.display_subtitle}`}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-gray-600">{u.email}</td>
                          <td className="py-3 px-3">{getRoleBadge(u.role)}</td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              {getStatusBadge(u)}
                              {isSuspended && u.suspension_category && (
                                <span className="text-xs text-gray-500">
                                  ({u.suspension_category})
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              {canManageStaff && (
                                <Button variant="ghost" size="sm" onClick={() => openEditStaff(u)}
                                  className="text-gray-600 hover:text-gray-900" title="Edit staff details"
                                  data-testid={`edit-staff-${u.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {canManageStaff && !isSuspended && !u.portal_access && (
                                <Button variant="ghost" size="sm" onClick={() => handleInvitePortal(u)}
                                  className="text-blue-600 hover:text-blue-800" title="Invite to portal access (login only — no role/authorization change)"
                                  disabled={actionLoading}
                                  data-testid={`invite-staff-${u.id}`}>
                                  <Send className="w-4 h-4" />
                                </Button>
                              )}
                              {u.portal_access && (
                                <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" title="Portal access granted">Portal</Badge>
                              )}
                              {canManageSuspension && !isSelf && !isSuperAdmin && (
                                <>
                                  {isSuspended ? (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleViewDetails(u)}
                                        className="text-blue-600 hover:text-blue-700"
                                        title="View Details"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleReactivate(u)}
                                        className="text-green-600 hover:text-green-700"
                                        title="Reactivate"
                                        disabled={actionLoading}
                                      >
                                        <UserCheck className="w-4 h-4" />
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleSuspendClick(u)}
                                      className="text-red-600 hover:text-red-700"
                                      title="Suspend"
                                    >
                                      <UserX className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                              {isSelf && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">You</Badge>
                              )}
                              {isSuperAdmin && !isSelf && (
                                <Badge className="bg-purple-100 text-purple-800 text-xs">Protected</Badge>
                              )}
                              {isOpsAdmin && !isSelf && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">Ops Admin</Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SOP Reference */}
        {canManageSuspension && (
          <Card className="mt-6 border-blue-200 bg-blue-50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900">Staff Suspension & Reactivation SOP</h3>
                  <ul className="text-sm text-blue-800 mt-2 space-y-1">
                    <li>• Only ownership/operations leadership may suspend staff</li>
                    <li>• Suspension requires a documented reason (category + optional notes)</li>
                    <li>• Suspension is non-punitive by default unless escalated</li>
                    <li>• Reactivation restores prior access unless security reset is required</li>
                    <li>• Permanent removal is a separate, explicit action</li>
                    <li>• All actions are logged for compliance and oversight</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Staff Portal Invite result modal */}
        {inviteResult && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="invite-result-modal">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" /> Portal Invite Created</h3>
                <button onClick={() => setInviteResult(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-gray-600 mb-3"><strong>{inviteResult.name}</strong> was granted portal login. Role, workforce classification, readiness and operational authorization are <strong>unchanged</strong>.</p>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Secure set-password link</label>
              <div className="flex items-center gap-2 mt-1">
                <input readOnly value={inviteResult.set_password_url || ''} data-testid="invite-link"
                  className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg bg-gray-50 font-mono" />
                <button onClick={() => { navigator.clipboard?.writeText(inviteResult.set_password_url || ''); }}
                  className="p-2 text-gray-600 hover:text-blue-700 border border-gray-300 rounded-lg" title="Copy" data-testid="invite-copy-btn"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-gray-400 mt-2">Expires {inviteResult.expires_at ? new Date(inviteResult.expires_at).toLocaleString() : 'soon'}. Single-use. Share securely — it grants no additional role or authorization.</p>
              <div className="flex justify-end mt-4">
                <Button onClick={() => setInviteResult(null)} data-testid="invite-result-close">Done</Button>
              </div>
            </div>
          </div>
        )}

        {/* Add / Edit Staff Modal */}
        {showStaffModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="staff-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {staffMode === 'add' ? <UserPlus className="w-5 h-5 text-blue-600" /> : <Pencil className="w-5 h-5 text-gray-600" />}
                  {staffMode === 'add' ? 'Add Staff Member' : 'Edit Staff Member'}
                </h3>
                <button onClick={() => setShowStaffModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>

              {staffError && (
                <div className="mb-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2" data-testid="staff-modal-error">{staffError}</div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">First name *</Label><Input value={staffForm.first_name} onChange={sf('first_name')} data-testid="staff-first-name" /></div>
                <div><Label className="text-xs">Last name *</Label><Input value={staffForm.last_name} onChange={sf('last_name')} data-testid="staff-last-name" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Email *</Label><Input type="email" value={staffForm.email} onChange={sf('email')} disabled={staffMode === 'edit'} data-testid="staff-email" />{staffMode === 'edit' && <p className="text-[10px] text-gray-400 mt-0.5">Email is the identity key and cannot be changed here.</p>}</div>
                <div><Label className="text-xs">Phone</Label><Input value={staffForm.phone} onChange={sf('phone')} data-testid="staff-phone" /></div>
                <div><Label className="text-xs">Title / Position</Label><Input value={staffForm.title} onChange={sf('title')} data-testid="staff-title" /></div>
                <div><Label className="text-xs">Department / Function</Label><Input value={staffForm.department} onChange={sf('department')} data-testid="staff-department" /></div>
                <div><Label className="text-xs">Manager / Supervisor</Label><Input value={staffForm.manager} onChange={sf('manager')} data-testid="staff-manager" /></div>
                <div>
                  <Label className="text-xs">Workforce classification</Label>
                  <select value={staffForm.workforce_classification} onChange={sf('workforce_classification')} className="w-full border rounded-md p-2 mt-1 text-sm" data-testid="staff-classification"
                    disabled={staffMode === 'edit' && !isSuperAdminUser}>
                    {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c || '— none —'}</option>)}
                  </select>
                  {staffMode === 'edit' && !isSuperAdminUser && <p className="text-[10px] text-gray-400 mt-0.5">Only a super_admin may change classification.</p>}
                </div>
                <div><Label className="text-xs">Start date</Label><Input type="date" value={staffForm.start_date} onChange={sf('start_date')} data-testid="staff-start-date" /></div>
                {staffMode === 'add' && (
                  <div>
                    <Label className="text-xs">Application access role</Label>
                    <select value={staffForm.role} onChange={sf('role')} className="w-full border rounded-md p-2 mt-1 text-sm" data-testid="staff-role">
                      {ASSIGNABLE_ROLES.filter((r) => isSuperAdminUser || !['admin', 'ops_admin'].includes(r)).map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <p className="text-[10px] text-gray-400 mt-0.5">Directory role only — no login is created. Invite portal access separately.</p>
                  </div>
                )}
                {staffMode === 'edit' && (
                  <div className="flex items-center gap-2 pt-5">
                    <input type="checkbox" checked={!!staffForm.is_active} onChange={(e) => setStaffForm((f) => ({ ...f, is_active: e.target.checked }))} data-testid="staff-active" />
                    <Label className="text-xs">Active</Label>
                  </div>
                )}
                <div className="sm:col-span-2"><Label className="text-xs">Internal notes</Label><textarea value={staffForm.internal_notes} onChange={sf('internal_notes')} className="w-full border rounded-md p-2 mt-1 h-20 resize-none text-sm" data-testid="staff-notes" /></div>
              </div>

              <div className="mt-3 text-[10px] text-gray-500 bg-gray-50 rounded px-2 py-1.5">
                Adding/editing here establishes identity and workforce relationship only. It does NOT mark the person Verified, Trained, Authorized or Operationally Eligible — those remain governed by onboarding/compliance workflows.
              </div>

              <div className="flex gap-3 justify-end mt-5">
                <Button variant="outline" onClick={() => setShowStaffModal(false)}>Cancel</Button>
                <Button onClick={saveStaff} className="bg-blue-600 hover:bg-blue-700" disabled={staffSaving} data-testid="staff-save-btn">
                  {staffSaving ? 'Saving...' : (staffMode === 'add' ? 'Add Staff' : 'Save Changes')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Modal */}
        {showSuspendModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6" data-testid="suspend-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Suspend Staff Member
                </h3>
                <button onClick={() => setShowSuspendModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="font-medium">{getUserDisplayName(selectedUser)}</div>
                <div className="text-sm text-gray-500">{selectedUser.email}</div>
                <div className="text-sm text-gray-500">Role: {selectedUser.role}</div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Suspension Category *</Label>
                  <select
                    value={suspendCategory}
                    onChange={(e) => setSuspendCategory(e.target.value)}
                    className="w-full border rounded-md p-2 mt-1"
                    data-testid="suspend-category-select"
                  >
                    <option value="">Select a category...</option>
                    {SUSPENSION_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">
                    Notes {suspendCategory === 'Other' ? '*' : '(optional)'}
                  </Label>
                  <textarea
                    value={suspendNotes}
                    onChange={(e) => setSuspendNotes(e.target.value)}
                    placeholder="Additional details about this suspension..."
                    className="w-full border rounded-md p-2 mt-1 h-24 resize-none"
                    data-testid="suspend-notes-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <Button variant="outline" onClick={() => setShowSuspendModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSuspend} 
                  className="bg-red-600 hover:bg-red-700"
                  disabled={actionLoading}
                  data-testid="confirm-suspend-btn"
                >
                  {actionLoading ? 'Suspending...' : 'Suspend User'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Suspension Details Modal */}
        {showDetailsModal && suspensionDetails && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6" data-testid="suspension-details-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Suspension Details</h3>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="font-medium text-red-900">{suspensionDetails.name || suspensionDetails.email}</div>
                  <div className="text-sm text-red-700">{suspensionDetails.email}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Category</Label>
                    <div className="font-medium">{suspensionDetails.suspension_category || 'N/A'}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Pre-Suspension Role</Label>
                    <div className="font-medium">{suspensionDetails.pre_suspension_role || suspensionDetails.role}</div>
                  </div>
                </div>

                {suspensionDetails.suspension_notes && (
                  <div>
                    <Label className="text-xs text-gray-500">Notes</Label>
                    <div className="p-2 bg-gray-50 rounded text-sm">{suspensionDetails.suspension_notes}</div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-gray-500">Suspended At</Label>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      {suspensionDetails.suspended_at ? new Date(suspensionDetails.suspended_at).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Suspended By</Label>
                    <div>{suspensionDetails.suspended_by_email || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleReactivate(selectedUser);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={actionLoading}
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Reactivate
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StaffDirectoryPage;
